
import React, { useEffect } from 'react';
import { Button } from '../../components/ui/Button';
import PlanCard from './components/PlanCard';
import BillingToggle from './components/BillingToggle';
import SubscriptionSelectionModal from './components/SubscriptionSelectionModal';
import { PageSkeleton } from '../../components/ui';
import { PageHeader } from '../../components/layout/Header';
import { useSubscription } from './hooks/useSubscription';
import { useAppContext } from '../../contexts/AppContext';
import { usePermissions } from '../../hooks/usePermissions';

const Subscription: React.FC = () => {
    const { setCurrentPage } = useAppContext();
    const { canViewBilling } = usePermissions();

    useEffect(() => {
        if (!canViewBilling) {
            setCurrentPage('profile');
        }
    }, [canViewBilling, setCurrentPage]);

    const {
        plans,
        subscription,
        stripeSubscription,
        stripePlans,
        isLoading,
        billingCycle,
        handleToggle,
        isSelectionModalOpen,
        planToConfigure,
        handleSelectPlan,
        handleConfirmSubscription,
        closeSelectionModal,
        navigateToBilling,
        isActive,
        isCheckoutLoading,
        hasPaidSubscription,
    } = useSubscription();

    if (!canViewBilling) {
        return null;
    }

    if (isLoading || !plans || plans.length === 0) {
        return <PageSkeleton variant="grid" mainCards={3} gridCols={3} />;
    }

    const currentPlanId = stripeSubscription?.planId;
    const currentPlanName = subscription?.planName;

    return (
        <>
            <main className="flex flex-col min-h-0 flex-1 max-md:px-4 max-md:pt-5 max-md:pb-6 md:px-6 md:py-8">
                <div className="mx-auto flex w-full min-w-0 max-w-5xl flex-1 flex-col max-md:gap-4 md:gap-8">
                    <PageHeader
                        title="Escolha seu plano"
                        subtitle="Planos simples e transparentes. Sem surpresas."
                        hideBackButton
                        className="mb-3 max-md:gap-1 md:relative md:mb-0 md:flex md:flex-col md:items-center md:justify-center md:gap-2 md:px-0 md:pb-4 [&>div:first-child]:md:items-center"
                        titleClassName="md:text-center md:text-heading-md text-secondary"
                        subtitleClassName="md:text-center"
                        actionClassName="max-md:w-full max-md:min-w-0 md:absolute md:right-0 md:top-0"
                        action={
                            isActive ? (
                                <Button
                                    onClick={navigateToBilling}
                                    variant="outline"
                                    size="lg"
                                    className="w-full justify-center rounded-xl border-default font-semibold text-secondary hover:bg-surface cursor-pointer max-md:py-3 md:w-auto md:py-3.5"
                                >
                                    Gerenciar cobrança
                                </Button>
                            ) : undefined
                        }
                    />

                    <section
                        aria-label="Ciclo de cobrança"
                        className="max-md:py-2 flex justify-center"
                    >
                        <BillingToggle
                            billingCycle={billingCycle}
                            onToggle={handleToggle}
                        />
                    </section>

                    <section aria-label="Planos disponíveis" className="min-w-0">
                        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-md:gap-3 md:gap-6 list-none p-0 m-0 min-w-0">
                            {plans.map((plan) => {
                                const isCurrent = currentPlanId
                                    ? plan.id === currentPlanId
                                    : plan.name === currentPlanName;

                                return (
                                    <li key={plan.id} className="flex md:m-0">
                                        <PlanCard
                                            plan={plan}
                                            billingCycle={billingCycle}
                                            isCurrentPlan={isCurrent}
                                            hasPaidSubscription={hasPaidSubscription}
                                            onSelectPlan={handleSelectPlan}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                </div>
            </main>

            <SubscriptionSelectionModal
                isOpen={isSelectionModalOpen}
                onClose={closeSelectionModal}
                plan={planToConfigure}
                billingCycle={billingCycle}
                currentUserCount={subscription?.usedSeats ?? 1}
                minUserCount={stripePlans.find(p => p.id === planToConfigure?.id)?.minUsers ?? 1}
                onConfirm={handleConfirmSubscription}
                isLoading={isCheckoutLoading}
            />
        </>
    );
};

export default Subscription;
